import {weakMemo} from '@packages/utilities/memo';
import {
  emptyArray,
  emptyObject,
  constant,
  constantTrue,
  constantFalse,
  concat,
  identity,
} from '@packages/utilities/fp/utils';
import {prepend} from '@packages/utilities/array';
import type {ExpressionPath} from '../ExpressionPath';

import {matchExpression} from '../matchExpression';
import {matchType, getTypeType} from '../matchType';
import {
  type Type,
  type DeconstructedRow,
  withMeta,
  withSystem,
  TRowCons,
  getMeta,
  getSystem,
  TTypeApplication,
  TGeneric,
} from '../DataTypeDefinition';
import {
  type Qualified,
  type TypeName,
  typeName,
  isValidIdentifier,
  namesEqual,
  iconName,
  getPluginName,
  getName,
  qualify,
  toHumanReadableString,
} from '@packages/systems/wfdl-Qualified';
import {reportBug} from '@packages/utilities/reporter';
import {name as pluginName} from '../Plugins/Names';
import {
  type PluginsType,
  getPlugins__DEPRECATED__DataTypes,
  getPluginsDataTypes,
} from '../Plugins/PluginsType';
import {
  functionTypeName,
  recordTypeName,
  TRecord,
  unionTypeName,
  TUnion,
  enumTypeName,
  TList,
  listTypeName,
  elementTypeName,
  type Expression,
} from '@packages/systems/wfdl-ast';
import type {QualifiedPluginIcon} from '../Plugins/PluginIconTypes';
import {replaceTypeRefs} from '../Checker/TypeCheckUtils';
import {name as componentName} from '../Component/Names';
import * as Expressions from '../Expressions';
import * as TypeImplementation from '../TypeImplementation';
import {TypePath} from '../TypePath';

const basicPlugin = pluginName('Basic');
const defaultIcon = qualify(basicPlugin, iconName('QuestionMark'));

/**
 * This function will return a single a DataType by name if it exists.
 */
export const getByName: (
  plugins: PluginsType,
  name: string | Qualified<TypeName>
) => TypeImplementation.TypeImplementation | null = (plugins, name) => {
  const dataType =
    typeof name === 'string'
      ? getPlugins__DEPRECATED__DataTypes(plugins)[name]
      : getPluginsDataTypes(plugins).get(name);
  if (!dataType) {
    const switchName = typeof name === 'string' ? name : getName(name);
    switch (switchName) {
      // These special-case types have no implementation, and we're
      // okay with that:
      case 'Record':
      case 'Union':
      case 'Function':
      case 'Generic':
      case 'TypeReference':
      case 'TypeApplication': {
        break;
      }
      default: {
        // Plugins aren't available in jest environment
        if (process.env.NODE_ENV === 'test') return null;

        if (process.env.NODE_ENV === 'development') {
          console.error(`Could not find shared dataType ${String(name)}.`);
        } else {
          // Please see https://github.com/webflow/webflow/pull/39900
          // eslint-disable-next-line webflow/better-bug-reporting
          reportBug(
            'UnknownAtomTypeKeyError',
            'Could not find shared dataType for the type',
            {
              name,
            }
          );
        }
      }
    }
    return null;
  }
  return dataType;
};

// formerly getDataTypeImplementation
export const getImplementation = (
  dataType: Type,
  plugins: PluginsType
): null | TypeImplementation.TypeImplementation => {
  const tyApp = deconstructTypeApplication(dataType);
  if (tyApp) {
    return getByName(plugins, tyApp.con);
  }
  if (isElementType(dataType)) {
    return getByName(plugins, 'Element');
  }
  return null;
};

const commonGetInnerType = (
  dataType: Type,
  plugins: PluginsType,
  innerTypeGetter: (t: TypeImplementation.TypeImplementation) => Type | null
) => {
  const tyApp = deconstructTypeApplication(dataType);
  if (!tyApp) return null;
  const impl = getByName(plugins, tyApp.con);
  if (!impl) return null;

  const ty = innerTypeGetter(impl);
  const params = TypeImplementation.getParams(impl);

  // If the type does not have params we can just return the implementation type as is
  if (!params || !ty) return ty;
  // Map the incoming arguments from `tyApp.args` into the defined list of params in the implementation `params`
  const argMap = params.reduce<Record<string, any>>((acc, curr, idx) => {
    const arg = tyApp.args[idx];
    acc[curr] = arg;
    return acc;
  }, {});

  // Then replace defined type references within `ty` with the help of the new `argMap`
  return replaceTypeRefs(argMap, ty);
};
export const getAliasType = (
  dataType: Type,
  plugins: PluginsType
): null | Type => {
  return commonGetInnerType(dataType, plugins, TypeImplementation.getAliasType);
};

export const getUniqueType = (
  dataType: Type,
  plugins: PluginsType
): null | Type => {
  return commonGetInnerType(
    dataType,
    plugins,
    TypeImplementation.getUniqueType
  );
};

function getIconType(
  dataType: Type,
  iconFromType: Type,
  plugins: PluginsType
): Type | null {
  const tyApp = deconstructTypeApplication(dataType);
  if (!tyApp) return null;

  const impl = getByName(plugins, tyApp.con);
  if (!impl) return null;

  const params = TypeImplementation.getParams(impl);
  if (!params) return null;

  // Map the incoming arguments from `tyApp.args` into the defined list of params in the implementation `params`
  const argMap = params.reduce<Record<string, any>>((acc, curr, idx) => {
    acc[curr] = tyApp.args[idx];
    return acc;
  }, {});

  return replaceTypeRefs(argMap, iconFromType);
}

const resolveImplementationIcon = TypeImplementation.matchIcon({
  IconByName: (val) => (_targetType: Type, _plugins: PluginsType) => val,
  IconFromType: (val) => (targetType: Type, plugins: PluginsType) => {
    const iconType = getIconType(targetType, val, plugins);
    return iconType ? getIcon(iconType, plugins) : defaultIcon;
  },
});

// formerly getDataTypeIcon
export const getIcon = (
  dataType: Type,
  plugins: PluginsType
): QualifiedPluginIcon => {
  const returnType = deconstructFunctionType(dataType).ret;
  const listType = getListItemType(returnType);
  const targetType =
    listType && isElementType(listType) ? listType : returnType;

  const impl = getImplementation(targetType, plugins);
  const implIcon = impl ? TypeImplementation.getIcon(impl) : undefined;

  if (implIcon) {
    return resolveImplementationIcon(implIcon)(targetType, plugins);
  }

  const aliasType = getAliasType(targetType, plugins);
  if (aliasType) return getIcon(aliasType, plugins);

  return defaultIcon;
};

// formerly getDataTypeDisplayName
// see deprecation plan at https://app.clickup.com/t/2310520/CORE-1203
export const __DEPRECATED__getDisplayName = (
  dataType: Type,
  plugins: PluginsType
): string => {
  const system = getSystem(dataType);

  if (system && system.displayName) {
    return system.displayName;
  }

  const dataTypeImplementation = getImplementation(dataType, plugins);

  const implDisplayName = dataTypeImplementation
    ? TypeImplementation.getDisplayName(dataTypeImplementation)
    : null;
  if (implDisplayName) {
    return implDisplayName;
  }

  if (isTypeListOfElements(dataType)) {
    return 'Text';
  }

  const name = getTypeConstructor(dataType);
  return name ? getName(name) : getTypeType(dataType);
};

/**
 * Get the fields of a record. If the provided `dataType` is not a record,
 * null is returned.
 */
export const getRecordFields = weakMemo(
  (
    dataType: Type
  ): null | {
    [key: string]: Type;
  } => {
    const tyApp = deconstructTypeApplication(dataType);
    if (
      tyApp &&
      namesEqual(tyApp.con, recordTypeName) &&
      tyApp.args.length === 1
    ) {
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      return deconstructRow(tyApp.args[0]).items;
    }
    return null;
  }
);

/**
 * Get the tail of a record. If the provided `dataType` is not a record,
 * null is returned.
 */
export const getRecordTail = weakMemo((dataType: Type): null | Type => {
  const tyApp = deconstructTypeApplication(dataType);
  if (isRecordType(dataType) && tyApp && tyApp.args.length === 1) {
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    const tail = deconstructRow(tyApp.args[0]).tail;
    return isRowEmpty(tail) ? null : tail;
  }
  return null;
});

const constantNull = (_: undefined | string) => null;
const constantEmptyObject = (_: undefined | string) => emptyObject;

const getEnumValues_: (arg1: Type) => (arg1: undefined | string) => null | {
  [key: string]: string;
} = matchType({
  RowCons:
    ({label, tail}) =>
    (externalLabel: undefined | string) => {
      const rest = getEnumValues_(tail)(getSystem(tail)?.label);
      if (rest == null) return null;
      return {...rest, [label]: externalLabel || label};
    },
  RowEmpty: (_) => constantEmptyObject,
  _: (_) => constantNull,
});

const getEnumArg = matchType({
  TypeApplication: ({con, arg}) => {
    const typeConstructor = getTypeConstructor(con);
    if (typeConstructor && namesEqual(typeConstructor, enumTypeName)) {
      return arg;
    }
    return null;
  },
  _: (_) => null,
});

/**
 * Get the values of an Enum. If the provided `dataType` is not an enum,
 * null is returned.
 */
export const getEnumValues = weakMemo(
  (
    dataType: Type
  ): null | {
    [key: string]: string;
  } => {
    const enumTyAppArg = getEnumArg(dataType);
    if (!enumTyAppArg) {
      return null;
    }

    return getEnumValues_(enumTyAppArg)(getSystem(enumTyAppArg)?.label);
  }
);

/**
 * Get the values of a Union. If the provided `dataType` is not a union,
 * null is returned.
 */
export const getUnionValues = (
  dataType: Type
): null | {
  [key: string]: Type;
} => {
  const tyApp = deconstructTypeApplication(dataType);
  if (
    tyApp &&
    namesEqual(tyApp.con, unionTypeName) &&
    tyApp.args.length === 1
  ) {
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    return deconstructRow(tyApp.args[0]).items;
  }
  return null;
};

export const getListItemType = (dataType: Type): null | Type => {
  const tyApp = deconstructTypeApplication(dataType);

  if (tyApp && namesEqual(tyApp.con, listTypeName) && tyApp.args.length === 1) {
    // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type | null'.
    return tyApp.args[0];
  }
  return null;
};

export const getElementTypeArgs = (
  dataType: Type
): null | {context: Type; state: Type} => {
  const tyApp = deconstructTypeApplication(dataType);
  if (!tyApp || !namesEqual(tyApp.con, elementTypeName)) return null;
  const [context, state] = tyApp.args;
  if (!context || !state) return null;
  return {context, state};
};

const updateRecordRow = (dataType: Type, fn: (arg1: Type) => Type): Type => {
  const tyApp = getTypeApplication(dataType);
  if (tyApp) {
    const {con, arg} = tyApp;
    const name = getTypeConstructor(con);
    if (name && namesEqual(name, recordTypeName)) {
      const newRow = fn(arg);
      if (newRow !== arg) {
        let updatedRow = TTypeApplication({
          con,
          arg: newRow,
        });
        const meta = getMeta(dataType);
        const system = getSystem(dataType);
        if (meta) {
          updatedRow = withMeta(meta, updatedRow);
        }
        if (system) {
          updatedRow = withSystem(system, updatedRow);
        }
        return updatedRow;
      }
    }
  }
  return dataType;
};

const updateRowItem = (
  row: Type,
  label: string,
  fn: (arg1: Type) => Type
): Type => {
  const rowCons = getRowCons(row);
  if (rowCons) {
    const {label: l, type, tail} = rowCons;
    if (l === label) {
      const newType = fn(type);
      if (newType === type) return row;
      return TRowCons({label, type: newType, tail});
    }
    const newTail = updateRowItem(tail, label, fn);
    if (newTail === tail) return row;
    return TRowCons({
      label: l,
      type,
      tail: newTail,
    });
  }
  return row;
};

export const updateRecordField = (
  dataType: Type,
  label: string,
  fn: (arg1: Type) => Type
): Type => updateRecordRow(dataType, (row) => updateRowItem(row, label, fn));

/**
 * Get the user facing label for a Data Type.
 */
export const getLabel = (dataType: Type): string => {
  const system = getSystem(dataType);
  if (system && typeof system.label === 'string') {
    // If `system.label` exists, use it:
    return system.label;
  }
  return '';
};

export const getToolTipText = (dataType: Type): string => {
  const system = getSystem(dataType);
  if (system && typeof system.toolTipText === 'string') {
    return sanitizeToolTipText(system.toolTipText);
  }
  return '';
};

const sanitizeToolTipText = (value: string): string => {
  return (
    value
      // Escape the comment terminator
      .replace(/\*\//g, '*\\/')
  );
};

/**
 * @deprecated Indexing into a `Type` using an `ExpressionPath` is nonsensical.
 */
export const __DEPRECATED__getTypeAtExpressionPath = (
  dataType: Type,
  path: ExpressionPath,
  plugins?: PluginsType
): Type | null => {
  if (path.length === 0) {
    return dataType;
  }

  const aliasType = plugins ? getAliasType(dataType, plugins) : null;
  if (aliasType) {
    return __DEPRECATED__getTypeAtExpressionPath(aliasType, path, plugins);
  }

  const [key, ...nextPath] = path;
  if (!key) return null;

  if (key.in === 'Record') {
    const fields = getRecordFields(dataType);
    if (!fields) return null;
    const fieldType = fields[key.at];
    return fieldType
      ? __DEPRECATED__getTypeAtExpressionPath(fieldType, nextPath, plugins)
      : null;
  }

  if (key.in === 'Union') {
    const unionValues = getUnionValues(dataType);

    if (!unionValues) return null;
    const unionCaseType = unionValues[key.tag];
    return unionCaseType
      ? __DEPRECATED__getTypeAtExpressionPath(unionCaseType, nextPath, plugins)
      : null;
  }

  if (key.in === 'List') {
    const listType = getListItemType(dataType);

    return listType
      ? __DEPRECATED__getTypeAtExpressionPath(listType, nextPath, plugins)
      : null;
  }

  if (key.in === 'Distinct') {
    if (!plugins) {
      return null;
    }
    const uniqueType = getUniqueType(dataType, plugins);
    return uniqueType
      ? __DEPRECATED__getTypeAtExpressionPath(uniqueType, nextPath, plugins)
      : null;
  }

  return null;
};

export const setTypeAtBindingPath = (
  dataType: Type | undefined,
  path: ExpressionPath,
  subDataType: Type
): Type => {
  if (path.length === 0) {
    return subDataType;
  }

  if (!dataType) {
    throw new TypeError('Zero-length path expected');
  }

  const [key, ...nextPath] = path;
  const system = getSystem(dataType);
  const meta = getMeta(dataType);

  // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
  if (key.in === 'Record') {
    return updateRecordField(dataType, key.at, (field) =>
      setTypeAtBindingPath(field, nextPath, subDataType)
    );
  }

  const unionValues = getUnionValues(dataType);
  if (unionValues) {
    // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
    if (key.in !== 'Union') {
      throw new TypeError(
        // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
        `Invalid path segment; expected 'Union', but got ${key.in}`
      );
    }

    const unionCaseType = unionValues[key.tag];
    if (!unionCaseType) {
      throw new TypeError(
        `Unexpected Union case tag: ${key.tag}; valid cases are ${Object.keys(
          unionValues
        )
          .map((v) => JSON.stringify(v))
          .join(', ')}`
      );
    }

    let workingType = TUnion({
      ...unionValues,
      [key.tag]: setTypeAtBindingPath(unionCaseType, nextPath, subDataType),
    });

    if (meta) {
      workingType = withMeta(meta, workingType);
    }

    if (system) {
      workingType = withSystem(system, workingType);
    }

    return workingType;
  }

  const listType = getListItemType(dataType);
  if (listType) {
    // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
    if (key.in !== 'List') {
      throw new TypeError(
        // @ts-expect-error - TS18048 - 'key' is possibly 'undefined'.
        `Invalid path segment; expected 'List', but got ${key.in}`
      );
    }

    let newListType = TList(
      setTypeAtBindingPath(listType, nextPath, subDataType)
    );

    if (meta) {
      newListType = withMeta(meta, newListType);
    }

    if (system) {
      newListType = withSystem(system, newListType);
    }

    return newListType;
  }
  // we should only get here if nothing matches before
  /** (dataType: empty) */
  throw new TypeError(`Unexpected Type: ${getTypeType(dataType)}`);
};

/**
 * Contextual data used by the printer in order to generate formatted,
 * human-readable syntax.
 */
type PrinterContext = {
  /**
   * The strings we use to wrap key-value pairs of row type construction.
   */
  rowsWrapper: {
    before: string;
    after: string;
  };
  /** The number of spaces we should currently be indented by. */
  depth: number;
};

// FIXME: Move the implementation of `print` into
// `@packages/systems/wfdl/codegen/wfdl` where it can reuse the following
// utilities/constants:
const TABSIZE = 2;

function indent(str: string, columns: number) {
  return ' '.repeat(columns) + str;
}

function wrap(
  before: string,
  body: Array<string>,
  after: string,
  depth: number = 0
) {
  if (body.length === 0) {
    return `${before} ${after}`;
  } else {
    return [before, list(body, depth), indent(after, depth)].join('\n');
  }
}

function list(listOfItems: Array<string>, depth: number): string {
  return listOfItems
    .map((str) => indent(str, depth + TABSIZE) + ',')
    .join('\n');
}

const defaultPrinterContext: PrinterContext = {
  rowsWrapper: {before: '{%', after: '%}'},
  depth: 0,
};

function printAttributes(attributes: {[key: string]: any}): string {
  let result = '';

  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value === 'undefined') {
      continue;
    }

    let attribute = '@' + key;

    if (typeof value !== 'boolean' || value === false) {
      const arg = JSON.stringify(value);
      attribute += `(${arg})`;
    }

    result += attribute + '\n';
  }

  // Remove last new line
  result = result.substring(0, result.length - 1);

  return result;
}

const printTypeDecoration = (ty: Type): string => {
  const system = getSystem(ty);
  const meta = getMeta(ty);

  let result = '';

  if (system) {
    result += printAttributes(system);
  }

  if (meta) {
    const attributes = printAttributes(meta);

    if (system) {
      result += '\n';
    }

    result += attributes;
  }

  return result;
};

function cutAttributesFromValue(
  input: string,
  ctx: PrinterContext
): {
  attributes: string;
  value: string;
} {
  const items = input.split('\n');
  const lastItem = items.pop();

  let attributes = '';

  if (items.length > 0) {
    const indentation = indent('', ctx.depth);
    attributes = items.join('\n' + indentation) + '\n' + indentation;
  }

  // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
  return {attributes, value: lastItem};
}

const bareCurlyBraces = {before: '{', after: '}'} as const;

const print_ =
  (ctx: PrinterContext) =>
  (ty: Type): string => {
    const undecoratedResult = (() => {
      const tyApp = deconstructTypeApplication(ty);

      if (tyApp) {
        const {con, args} = tyApp;

        // `Function`:
        if (namesEqual(con, functionTypeName)) {
          if (args.length > 1) {
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            let argString = print_(ctx)(args[0]);
            // We peek ahead to determine if our arg needs to be wrapped with
            // parentheses:
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            const argTyApp = deconstructTypeApplication(args[0]);
            const argIsFunc =
              argTyApp && namesEqual(argTyApp.con, functionTypeName);
            if (argIsFunc) argString = `(${argString})`;
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            return `${argString} => ${print_(ctx)(args[1])}`;
          }
        }

        // `Record`:
        if (namesEqual(con, recordTypeName)) {
          if (args.length === 1) {
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            if (isRowEmpty(args[0])) return '{}';

            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            return print_({...ctx, rowsWrapper: bareCurlyBraces})(args[0]);
          }
        }

        // `Enum`:
        if (namesEqual(con, enumTypeName)) {
          if (args.length === 1) {
            const enumValues = getEnumValues(ty);
            if (enumValues) {
              const labels = Object.keys(enumValues);
              if (labels.length === 0) return 'Enum<{}>';
              const itemStrings = labels.map((key) => {
                const keyStr = isValidIdentifier(key)
                  ? key
                  : JSON.stringify(key);
                return `${keyStr}: ${JSON.stringify(enumValues[key])}`;
              });
              return `Enum<${wrap('{', itemStrings, '}', ctx.depth)}>`;
            }
          }
        }

        // General type application (includes `Union`):
        const conString =
          getPluginName(con) === 'Builtin'
            ? getName(con)
            : toHumanReadableString(con);
        if (!args.length) return conString;
        const newCtx =
          conString === 'Union' ? {...ctx, rowsWrapper: bareCurlyBraces} : ctx;
        return `${conString}<${args.map(print_(newCtx)).join(', ')}>`;
      }

      const ref = getTypeReference(ty);
      if (ref) return ref;

      const tyGeneric = getGenericType(ty);
      if (tyGeneric) {
        const args: Array<string> = [];
        let nextGeneric: ReturnType<typeof getGenericType> = tyGeneric;
        let lastGeneric = nextGeneric;
        while (nextGeneric) {
          args.push(nextGeneric.arg);
          lastGeneric = nextGeneric;
          nextGeneric = getGenericType(nextGeneric.body);
        }
        return `<${args.join(', ')}>(${print_(ctx)(lastGeneric.body)})`;
      }

      if (isRowEmpty(ty)) return 'RowEmpty';

      const {items, tail} = deconstructRow(ty);
      const labels: Array<string> = Object.keys(items);

      if (labels.length) {
        const nextCtx = {
          ...ctx,
          depth: ctx.depth + TABSIZE,
          rowsWrapper: defaultPrinterContext.rowsWrapper,
        } as const;
        const itemStrings = labels.map((l) => {
          const key = `${isValidIdentifier(l) ? l : JSON.stringify(l)}`;
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          const initialValue = print_(nextCtx)(items[l]);

          // Nested records always start with a curly brace, see the "nested records with attributes" test for an example:
          // https://github.com/webflow/webflow/blob/12842dd7c4cda14e7b083d248a2f65ad2f6b9e16/packages/systems/wfdl/tools/typeSyntaxTestCases.js#L399
          const isNested = initialValue.includes('{');
          if (isNested) {
            return `${key}: ${initialValue}`;
          }

          // Cut the meta and system attributes from the value,
          // so that we can put them above the key like this:
          // {
          //   alt: Text,
          //   @isBindable
          //   @label("Image")
          //   @variant("small")
          //   img: Asset::Image,
          // }
          const {attributes, value} = cutAttributesFromValue(
            initialValue,
            nextCtx
          );

          return `${attributes}${key}: ${value}`;
        });

        if (!isRowEmpty(tail)) {
          itemStrings.push(`...${print_(nextCtx)(tail)}`);
        }

        return wrap(
          ctx.rowsWrapper.before,
          itemStrings,
          ctx.rowsWrapper.after,
          ctx.depth
        );
      }

      const unknown = getUnknownType(ty);
      if (unknown) return `unknown${unknown.id}`;

      const rigid = getRigidType(ty);
      if (rigid) return String(rigid);

      return getTypeType(ty);
    })();

    const decoration = printTypeDecoration(ty);

    if (!decoration) {
      return undecoratedResult;
    }

    return `${decoration}\n${undecoratedResult}`;
  };

/**
 * Returns a human-friendly string representation of a Type.
 * Userful for error messages and debugging.
 */
export const print = print_(defaultPrinterContext);

/**
 * Given a function for comparing 2 values for equality, this function returns
 * a function for comparing 2 object maps for equality.
 */
const mapMatcher =
  <a>(
    valCompare: (arg1: a, arg2: a) => boolean
  ): ((
    arg1: {
      [key: string]: a;
    },
    arg2: {
      [key: string]: a;
    }
  ) => boolean) =>
  (
    obj1: {
      [key: string]: a;
    },
    obj2: {
      [key: string]: a;
    }
  ) => {
    const obj1Keys = Object.keys(obj1);
    const obj2Keys = Object.keys(obj2);
    return (
      obj1Keys.length === obj2Keys.length &&
      obj1Keys.every(
        // @ts-expect-error - TS2345 - Argument of type 'a | undefined' is not assignable to parameter of type 'a'.
        (k) => obj2.hasOwnProperty(k) && valCompare(obj1[k], obj2[k])
      )
    );
  };

/**
 * Given 2 serializable data types this function returns `true` if they
 * are equal. Metadata is not taken into consideration for comparisons.
 */
export const equal = (t1: Type, t2: Type): boolean => {
  const enumValues1 = getEnumValues(t1);
  const enumValues2 = getEnumValues(t2);

  if (enumValues1 && enumValues2) {
    return keysMatch(enumValues1, enumValues2);
  }

  const fields1 = getRecordFields(t1);
  const fields2 = getRecordFields(t2);

  if (fields1 && fields2) {
    return itemTypesMatch(fields1, fields2);
  }

  const unionValues1 = getUnionValues(t1);
  const unionValues2 = getUnionValues(t2);

  if (unionValues1 && unionValues2) {
    return itemTypesMatch(unionValues1, unionValues2);
  }

  const tyGeneric1 = getGenericType(t1);
  const tyGeneric2 = getGenericType(t2);

  if (tyGeneric1 && tyGeneric2) {
    // TODO: Generic can be equal even if arg names differ
    return (
      tyGeneric1.arg === tyGeneric2.arg &&
      equal(tyGeneric1.body, tyGeneric2.body)
    );
  }

  if (tyGeneric2) {
    return equal(t1, tyGeneric2.body);
  }

  const ref1 = getTypeReference(t1);
  const ref2 = getTypeReference(t2);

  if (ref1 && ref2) {
    // TODO: TypeReference with same name don’t necessarily refer to the same type
    // ```js
    // // Example 1
    // Generic({
    //   arg: 'a',
    //   body: Record({
    //     foo: TypeReference('a'),
    //     bar: Generic({
    //       arg: 'a',
    //       // This `a` is not the same as the previous one.
    //       // The previous one is shadowed.
    //       body: TypeReference('a')
    //     })
    //   })
    // })
    //
    //  // Example 2
    //  Record({
    //    foo: Generic({
    //      arg: 'a',
    //      body: TypeReference('a')
    //    })
    //    bar: Generic({
    //      arg: 'a',
    //      // This is not the same `a` as the previous one because
    //      // we are in 2 different Generic scopes.
    //      body: TypeReference('a')
    //    })
    //  })
    //  ```
    return ref1 === ref2;
  }

  // A type variable can stand in place of any type
  if (ref2) return true;

  const tyName1 = getTypeConstructor(t1);
  const tyName2 = getTypeConstructor(t2);

  if (tyName1 && tyName2) return namesEqual(tyName1, tyName2);

  const tyApp1 = getTypeApplication(t1);
  const tyApp2 = getTypeApplication(t2);

  if (tyApp1 && tyApp2) {
    return equal(tyApp1.con, tyApp2.con) && equal(tyApp1.arg, tyApp2.arg);
  }

  // Primitive types other than Enum are left here.
  return getTypeType(t1) === getTypeType(t2);
};

// By always returning `true` from the value comparator we are essentially
// saying we are only interested in checking if keys are the same for
// the passed in maps.
const keysMatch = mapMatcher(() => true);

// Check maps of serializable types for equality.
const itemTypesMatch = mapMatcher(equal);

export const unwrapAliasType = (ty: Type, plugins: PluginsType): Type => {
  const aliasType = getAliasType(ty, plugins);
  if (aliasType) {
    return unwrapAliasType(aliasType, plugins);
  }
  return ty;
};

export const deconstructTypeApplication = weakMemo(
  (
    dataType: Type
  ): {
    con: Qualified<TypeName>;
    args: Array<Type>;
  } | null => {
    const tyName = getTypeConstructor(dataType);
    if (tyName) return {con: tyName, args: emptyArray};

    const tyApp = getTypeApplication(dataType);
    if (tyApp) {
      const inner = deconstructTypeApplication(tyApp.con);
      return inner
        ? {con: inner.con, args: inner.args.concat([tyApp.arg])}
        : inner;
    }
    return null;
  }
);

export const deconstructGeneric: (arg1: Type) => {
  vars: Array<string>;
  body: Type;
} = matchType({
  Generic: ({arg, body}) => {
    const rest = deconstructGeneric(body);
    return {vars: concat(rest.vars)([arg]), body: rest.body};
  },
  _: (body) => ({vars: emptyArray, body}),
});

export const deconstructRow = weakMemo((row: Type): DeconstructedRow => {
  const rowCons = getRowCons(row);
  if (rowCons) {
    const {items, tail} = deconstructRow(rowCons.tail);
    return {
      items: {...items, [rowCons.label]: rowCons.type},
      tail,
    };
  }
  return {items: emptyObject, tail: row};
});

/**
 * Given a type that is supposed to be a function, return the arg and ret types.
 */
export const getFunctionArgAndRetTypes = (
  dataType: Type
): {
  arg: Type;
  ret: Type;
} | null => {
  const typeApp = deconstructTypeApplication(dataType);
  if (
    !typeApp ||
    !namesEqual(typeApp.con, functionTypeName) ||
    typeApp.args.length !== 2
  )
    return null;
  const [arg, ret] = typeApp.args;
  // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'. | TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
  return {arg, ret};
};

export const deconstructFunctionType = (
  ty: Type
): {
  tyArgs: Array<string>;
  args: Array<Type>;
  ret: Type;
} => {
  const generic = getGenericType(ty);

  if (generic) {
    const {tyArgs, args, ret} = deconstructFunctionType(generic.body);
    return {tyArgs: prepend(generic.arg, tyArgs), args, ret};
  }

  const argAndRet = getFunctionArgAndRetTypes(ty);
  if (!argAndRet) return {tyArgs: emptyArray, args: emptyArray, ret: ty};
  const rest = deconstructFunctionType(argAndRet.ret);

  return {
    tyArgs: rest.tyArgs,
    args: prepend(argAndRet.arg, rest.args),
    ret: rest.ret,
  };
};

export const getDefaultValue = (dataType: Type, fallbackDefault: any): any => {
  const system = getSystem(dataType);
  if (system && system.hasOwnProperty('defaultValue')) {
    return system.defaultValue;
  }
  return fallbackDefault;
};

const getTypeDefaultValueWithType = matchType<
  (arg1: PluginsType, arg2: Type) => any
>({
  TypeConstructor: (name) => (plugins: PluginsType, dataType: Type) => {
    if (namesEqual(name, richTextChildrenType)) {
      return getDefaultValue(dataType, []);
    }
    const pluginDataType = getByName(plugins, name);
    // Allow the plugin instance to override the defaultValue; otherwise fall
    // back to the default value specified in the plugin implementation:
    return getDefaultValue(
      dataType,
      pluginDataType
        ? TypeImplementation.getDefaultValue(pluginDataType)('')
        : ''
    );
  },
  Element: constant((_, dataType) => {
    const defaultValue = getDefaultValue(dataType, {
      id: 'new-element-temp',
      text: true,
      data: {value: 'temp'},
      children: [],
      type: null,
    });
    return defaultValue;
  }),
  Generic:
    ({body}) =>
    (plugins: PluginsType) =>
      getTypeDefaultValue(plugins)(body),
  TypeApplication:
    ({con}) =>
    (plugins: PluginsType, dataType: Type) => {
      const name = getTypeConstructor(con);

      if (name && namesEqual(name, enumTypeName)) {
        const pluginDataType = getByName(plugins, name);
        // Allow the plugin instance to override the defaultValue; otherwise fall
        // back to the default value specified in the plugin implementation:
        return getDefaultValue(
          dataType,
          pluginDataType
            ? TypeImplementation.getDefaultValue(pluginDataType)('')
            : ''
        );
      }

      return getTypeDefaultValue(plugins)(con);
    },
  _: constant(
    () =>
      // What is a default value for a type that can be anything?
      undefined
  ),
});

export const getTypeDefaultValue = weakMemo((plugins: PluginsType) =>
  weakMemo((dataType: Type): any => {
    // First look at the defaultValue from the provided type, then if not
    // found move forward according to the provided type shape.
    const intialDefault = getDefaultValue(dataType, undefined);
    if (intialDefault !== undefined) {
      return intialDefault;
    }
    // In order to correctly return defaultValues from defaults we
    // recurse down a layer to the Alias type definition.
    const aliasType = getAliasType(dataType, plugins);
    if (aliasType) {
      return getTypeDefaultValue(plugins)(aliasType);
    }
    const fields = getRecordFields(dataType);
    if (fields) {
      return Object.entries(fields).reduce<Record<string, any>>(
        (acc, [key, value]) => {
          acc[key] = getTypeDefaultValue(plugins)(value);
          return acc;
        },
        {}
      );
    }

    const unionValues = getUnionValues(dataType);
    if (unionValues) {
      // We need a `unionTagFallback` in order to handle cases where the
      // Type author has not provided a default for the Union. We just pick
      // the fist "value" here as the default in this case.
      const unionTagFallback = Object.keys(unionValues)[0];
      const tag = getDefaultValue(dataType, unionTagFallback);
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      const val = getTypeDefaultValue(plugins)(unionValues[tag]);

      return {tag, val};
    }

    const listType = getListItemType(dataType);
    if (listType) {
      return getDefaultValue(dataType, []);
    }

    return getTypeDefaultValueWithType(dataType)(plugins, dataType);
  })
);

const containsVariable = Expressions.some(Expressions.isVariable);

const isAnyChildrenAlreadyBound = (expr: Expression) => {
  if (!expr || !Expressions.isList(expr)) {
    return false;
  }
  return containsVariable(expr);
};

const stringElementType = qualify(pluginName('Basic'), componentName('String'));
const lineBreakElementType = qualify(
  pluginName('Basic'),
  componentName('LineBreak')
);
const strongElementType = qualify(pluginName('Basic'), componentName('Strong'));
const emphasizedElementType = qualify(
  pluginName('Basic'),
  componentName('Emphasized')
);
const superscriptElementType = qualify(
  pluginName('Basic'),
  componentName('Superscript')
);
const subscriptElementType = qualify(
  pluginName('Basic'),
  componentName('Subscript')
);
const spanElementType = qualify(pluginName('Basic'), componentName('Span'));
const linkElementType = qualify(pluginName('Basic'), componentName('Link'));

const isTextElement = matchExpression({
  Element: (val) =>
    namesEqual(val.type, stringElementType) ||
    namesEqual(val.type, lineBreakElementType) ||
    namesEqual(val.type, strongElementType) ||
    namesEqual(val.type, emphasizedElementType) ||
    namesEqual(val.type, superscriptElementType) ||
    namesEqual(val.type, subscriptElementType) ||
    namesEqual(val.type, spanElementType) ||
    namesEqual(val.type, linkElementType),
  _: constantTrue,
});
const isListWithoutTextItems = matchExpression({
  List: (val) => !val.every(isTextElement),
  _: constantFalse,
});
const doesChildrenContainNonTextItems = (expr: Expression) =>
  expr ? isListWithoutTextItems(expr) : false;

export const isPrivate = (
  dataType: Type | undefined,
  plugins: PluginsType,
  expr?: Expression
): boolean => {
  if (!dataType) {
    return false;
  }

  const system = getSystem(dataType);

  const fields = getRecordFields(dataType);
  if (fields) {
    // If we have a record for which all fields are
    // private when can return the whole record as private
    const record = expr ? Expressions.getRecord(expr) : null;
    const allFieldsPrivate = Object.keys(fields).every((field) =>
      record
        ? isPrivate(fields[field], plugins, record[field])
        : isPrivate(fields[field], plugins)
    );
    if (allFieldsPrivate) {
      return true;
    }
  }

  // The Type system.private takes precedence, but only when explicitly
  // specified...
  if (system && typeof system.private === 'boolean') {
    return system.private;
  }

  // ...otherwise we rely on the Type implementation's system.private...
  const tyApp = deconstructTypeApplication(dataType);
  const dataTypeImplementation = getByName(
    plugins,
    tyApp ? tyApp.con : getTypeType(dataType)
  );
  if (!dataTypeImplementation) {
    return false;
  }

  // Two escape hatch checks that are dependant on the `expr` being passed in
  if (expr && (isElementType(dataType) || isRichTextChildrenType(dataType))) {
    return isAnyChildrenAlreadyBound(expr);
  } else if (expr && isTypeListOfElements(dataType)) {
    return doesChildrenContainNonTextItems(expr);
  }

  // ...or a regular boolean:
  return Boolean(TypeImplementation.getPrivate(dataTypeImplementation));
};

/**
 * Checks whether the type or any of its subtypes satisfies the predicate.
 */
export const anyType = (
  predicate: (arg1: Type) => boolean
): ((arg1: Type) => boolean) => {
  const anyTypeWithType = matchType({
    RowCons: ({type, tail}) => go(type) || go(tail),
    Generic: ({body}) => go(body),
    TypeApplication: ({con, arg}) => go(con) || go(arg),
    _: (_) => false,
  });
  const go = (ty: Type): boolean => {
    const res = predicate(ty);
    if (res) return res;

    return anyTypeWithType(ty);
  };
  return go;
};

/**
 * Generates an ID based on the plugin type and any applicable label.
 * used for automated testing purposes (e.g. Cypress).
 */
export const generateAutomationIds = (dataType: Type) => {
  const tyApp = deconstructTypeApplication(dataType);
  let base = `Type--Plugin_${
    // TODO: Make use of the plugin name too: https://app.clickup.com/t/2310520/CORE-551
    tyApp ? getName(tyApp.con) : getTypeType(dataType)
  }`;

  const system = getSystem(dataType);
  const meta = getMeta(dataType);

  if (system && system.label) {
    base = `${base}_${system.label}`;
  }

  if (
    tyApp &&
    (namesEqual(tyApp.con, enumTypeName) ||
      namesEqual(tyApp.con, unionTypeName))
  ) {
    base = `${base}_${(meta && meta.variant) || 'buttons'}`;
  }

  return base;
};

/**
 * Checks if a given dataType is one of `children` aka List<Element>
 */
export const isTypeListOfElements = (dataType: Type): boolean => {
  const listType = getListItemType(dataType);
  return Boolean(listType && isElementType(listType));
};

/**
 * Checks if the provided type is a Record type
 */
export const isRecordType = matchType({
  TypeApplication: ({con}) => {
    const name = getTypeConstructor(con);
    return Boolean(name && namesEqual(name, recordTypeName));
  },
  _: constant(false),
});

export const isElementType = matchType({
  Element: constantTrue,
  TypeApplication: (_, dataType) => {
    const tyApp = deconstructTypeApplication(dataType);
    return Boolean(tyApp && namesEqual(tyApp.con, elementTypeName));
  },
  _: constantFalse,
});

export const getGenericType = matchType({
  Generic: identity,
  _: constant(null),
});

export const getRigidType = matchType({
  Rigid: identity,
  _: constant(null),
});

export const isRowCons = matchType({
  RowCons: constantTrue,
  _: constantFalse,
});

export const getRowCons = matchType({
  RowCons: identity,
  _: constant(null),
});

export const isRowEmpty = matchType({
  RowEmpty: constantTrue,
  _: constantFalse,
});

export const isTypeApplication = matchType({
  TypeApplication: constantTrue,
  _: constantFalse,
});

export const getTypeApplication = matchType({
  TypeApplication: identity,
  _: constant(null),
});

export const isTypeConstructor = matchType({
  TypeConstructor: constantTrue,
  _: constantFalse,
});

export const getTypeConstructor = matchType({
  TypeConstructor: identity,
  _: constant(null),
});

export const getTypeReference = matchType({
  TypeReference: identity,
  _: constant(null),
});

export const isUnknownType = matchType({
  Unknown: constantTrue,
  _: constantFalse,
});

export const getUnknownType = matchType({
  Unknown: identity,
  _: constant(null),
});

export const isGeneric = matchType({
  Generic: constantTrue,
  _: constantFalse,
});

/**
 * Checks whether a given dataType is a list of elements or a
 * Rich Text element.
 */
export const isListOfElements = (dataType: Type): boolean => {
  if (dataType == null) return false;

  //  --- Old style ---
  if (
    // @ts-expect-error - keeping old way of accessing type here on purpose
    dataType.type === 'List' &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    dataType.of.type === 'Element'
  ) {
    return true;
  }

  // --- New style ---
  if (isTypeListOfElements(dataType)) return true;
  if (isSlotContentType(dataType)) return true;

  return isRichTextChildrenType(dataType);
};

// Hack RTC "Unique" Type utils

const richTextChildrenType = qualify(basicPlugin, typeName('RichTextChildren'));
const slotContentType = qualify(pluginName('Slots'), typeName('SlotContent'));
const conditionsOperandType = qualify(
  pluginName('ConditionsOperand'),
  typeName('Operand')
);
const altTextType = qualify(pluginName('Basic'), typeName('AltText'));
const textType = qualify(pluginName('Builtin'), typeName('Text'));
const styleVariantType = qualify(pluginName('Basic'), typeName('StyleVariant'));
const imageType = qualify(pluginName('Asset'), typeName('Image'));
const headingTagType = qualify(pluginName('Basic'), typeName('HeadingTag'));
const visibilityConditionsType = qualify(
  pluginName('Visibility'),
  typeName('VisibilityConditions')
);
const devlinkRuntimePropsType = qualify(
  pluginName('Devlink'),
  typeName('RuntimeProps')
);
const devlinkSlotType = qualify(pluginName('Devlink'), typeName('Slot'));
const booleanTypeName = qualify(pluginName('Boolean'), typeName('Boolean'));
const basicBooleanType = qualify(pluginName('Basic'), typeName('Boolean'));
const numberType = qualify(pluginName('Builtin'), typeName('Number'));

export const isRichTextChildrenType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, richTextChildrenType)
  );
};

export const isSlotContentType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, slotContentType)
  );
};

export const isAltTextType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, altTextType)
  );
};

export const isTextType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, textType)
  );
};

export const isStyleVariantType = (ty: Type): boolean => {
  const deconstructedType = deconstructTypeApplication(ty);
  return Boolean(
    deconstructedType?.con &&
    namesEqual(deconstructedType.con, styleVariantType)
  );
};

const variableModeTypeName = qualify(
  pluginName('Basic'),
  typeName('VariableMode')
);

export const isVariableModeType = (ty: Type): boolean => {
  const deconstructedType = deconstructTypeApplication(ty);
  return Boolean(
    deconstructedType?.con &&
    namesEqual(deconstructedType.con, variableModeTypeName)
  );
};

export const isImageType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, imageType)
  );
};

export const isHeadingTagType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, headingTagType)
  );
};

export const isVisibilityConditionsType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName &&
    namesEqual(typeConstructorName, visibilityConditionsType)
  );
};

export const isBooleanType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, booleanTypeName)
  );
};

export const isBooleanLikeType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName &&
    (namesEqual(typeConstructorName, booleanTypeName) ||
      namesEqual(typeConstructorName, basicBooleanType))
  );
};

export const isNumberType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, numberType)
  );
};

export const isDevlinkRuntimePropsType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName &&
    namesEqual(typeConstructorName, devlinkRuntimePropsType)
  );
};

export const isDevlinkSlotType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, devlinkSlotType)
  );
};

export const attributesTypeName = qualify(
  pluginName('Basic'),
  typeName('Attributes')
);

export const isAttributesType = (ty: Type): boolean => {
  const typeConstructorName = getTypeConstructor(ty);
  return Boolean(
    typeConstructorName && namesEqual(typeConstructorName, attributesTypeName)
  );
};

export const isConditionsOperandType = (ty: Type): boolean => {
  const deconstructedType = deconstructTypeApplication(ty);
  return Boolean(
    deconstructedType?.con &&
    namesEqual(deconstructedType.con, conditionsOperandType)
  );
};

// Merge the system and meta information from two types. Prioritize the
// "incoming" over "current". Does not merge deeply. Only top level keys.
const mergeSystemAndMetaFromTypes = (currentTy: Type, incomingTy: Type) => {
  const incomingSys = getSystem(incomingTy);
  let system = null;
  if (incomingSys) {
    system = incomingSys;
  }
  const currentSys = getSystem(currentTy);
  if (currentSys) {
    system = system ? {...currentSys, ...system} : currentSys;
  }

  const incomingMeta = getMeta(incomingTy);
  let meta = null;
  if (incomingMeta) {
    meta = incomingMeta;
  }
  const currentMeta = getMeta(currentTy);
  if (currentMeta) {
    meta = meta ? {...currentMeta, ...meta} : currentMeta;
  }

  return {system, meta};
};

/**
 * Recursively merges two type objects.
 * Will continue to recurse until it hits a type field that is not a Record.
 * When current and incoming type object contain contain the same field at the same level,
 * the incoming field is prioritized over the current field.
 * When current and incoming type objects both have Record tails, the incoming tail is prioritized.
 * The incoming type is prioritized for object-key order and thus used as the base
 * object from where we build it up.
 */
export const deepMergeTypes = (
  current: {
    [key: string]: Type;
  },
  incoming: {
    [key: string]: Type;
  }
): {
  [key: string]: Type;
} => {
  const res = {...incoming};
  Object.keys(current).forEach((key) => {
    if (key in incoming) {
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      const currentFields = getRecordFields(current[key]);
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      const incomingFields = getRecordFields(incoming[key]);
      if (currentFields && incomingFields) {
        // Merge in the system and meta information, incoming type is
        // prioritized when fields found in both
        const {system, meta} = mergeSystemAndMetaFromTypes(
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          current[key],
          incoming[key]
        );
        const mergedTypes = deepMergeTypes(currentFields, incomingFields);
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        const currentTail = getRecordTail(current[key]);
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        const incomingTail = getRecordTail(incoming[key]);
        let recordType;
        if (incomingTail) {
          recordType = TRecord(mergedTypes, incomingTail);
        } else if (currentTail) {
          recordType = TRecord(mergedTypes, currentTail);
        } else {
          recordType = TRecord(mergedTypes);
        }
        const typeWithSystem = system
          ? withSystem(system, recordType)
          : recordType;
        const typeWithMeta = meta
          ? withMeta(meta, typeWithSystem)
          : typeWithSystem;
        res[key] = typeWithMeta;
      } else {
        // Prioritize incoming field if the keys are not records
        // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
        res[key] = incoming[key];
      }
    } else {
      // If current[key] not found in incoming type at all simply
      // set that value on to our aggregated `res` type
      // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
      res[key] = current[key];
    }
  });

  return res;
};

const withOriginalSystemMetaFactory =
  (original: Type) => (updatedType: Type) => {
    const system = getSystem(original);
    const meta = getMeta(original);

    if (system) {
      return withSystem(system, updatedType);
    }

    if (meta) {
      return withMeta(meta, updatedType);
    }

    return updatedType;
  };

export function sparseUpdateIn(
  type: Type,
  path: TypePath,
  update: (type: Type) => Type
): Type {
  if (!path.length) return update(type);

  const [head, ...rest] = path;

  if (!head) return type;

  const withOriginalSystemMeta = withOriginalSystemMetaFactory(type);

  switch (head.in) {
    case 'TypeApplication': {
      const typeApp = getTypeApplication(type);
      if (!typeApp) return type;

      const {at} = head;

      switch (at) {
        case 'con': {
          const updatedCon = sparseUpdateIn(typeApp.con, rest, update);
          if (updatedCon === typeApp.con) return type;

          return withOriginalSystemMeta(
            TTypeApplication({
              con: sparseUpdateIn(typeApp.con, rest, update),
              arg: typeApp.arg,
            })
          );
        }
        case 'arg': {
          const updatedArg = sparseUpdateIn(typeApp.arg, rest, update);
          if (updatedArg === typeApp.arg) return type;

          return withOriginalSystemMeta(
            TTypeApplication({
              con: typeApp.con,
              arg: updatedArg,
            })
          );
        }
        default:
          at satisfies never;
      }
      break;
    }
    case 'Generic': {
      const generic = getGenericType(type);
      if (!generic) return type;

      const updatedBody = sparseUpdateIn(generic.body, rest, update);
      if (updatedBody === generic.body) return type;

      return withOriginalSystemMeta(
        TGeneric({
          arg: generic.arg,
          body: updatedBody,
        })
      );
    }
    case 'RowCons': {
      const rowCons = getRowCons(type);
      if (!rowCons) return type;

      const {at} = head;

      switch (at) {
        case 'tail': {
          const updatedTail = sparseUpdateIn(rowCons.tail, rest, update);
          if (updatedTail === rowCons.tail) return type;

          return withOriginalSystemMeta(
            TRowCons({
              label: rowCons.label,
              type: rowCons.type,
              tail: updatedTail,
            })
          );
        }
        case 'type': {
          const updateRowConType = sparseUpdateIn(rowCons.type, rest, update);
          if (updateRowConType === rowCons.type) return type;

          return withOriginalSystemMeta(
            TRowCons({
              label: rowCons.label,
              type: updateRowConType,
              tail: rowCons.tail,
            })
          );
        }
        default:
          at satisfies never;
      }

      break;
    }
    default:
      head satisfies never;
  }

  return type;
}
